import * as d3 from 'd3';

import { StringOrDateValue } from '../../models';

export interface IsparklineHelperService {
    resize(width: number, height: number): this;
    data(data: StringOrDateValue[]): this;
    xDomain(): [Date, Date];
    xDomain(domain: [Date, Date]): this;
    yDomain(): [number, number];
    yDomain(domain: [number, number]): this;
    scaleY(value: number): number;
    path(data: StringOrDateValue[], showGap?: boolean): string;
}

class LineSimplifier {
    /*
    (c) 2013, Vladimir Agafonkin
    Simplify.js, a high-performance JS polyline simplification library
    mourner.github.io/simplify-js
    */

    // to suit your point format, run search/replace for '.x' and '.y';
    // for 3D version, see 3d branch (configurability would draw significant performance overhead)

    // square distance between 2 points
    private getSqDist(p1, p2) {

        const dx = p1.x - p2.x,
              dy = p1.y - p2.y;

        return dx * dx + dy * dy;
    }

    // square distance from a point to a segment
    private getSqSegDist(p, p1, p2) {

        let x = p1.x,
            y = p1.y,
            dx = p2.x - x,
            dy = p2.y - y;

        if (dx !== 0 || dy !== 0) {

            const t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy);

            if (t > 1) {
                x = p2.x;
                y = p2.y;

            } else if (t > 0) {
                x += dx * t;
                y += dy * t;
            }
        }

        dx = p.x - x;
        dy = p.y - y;

        return dx * dx + dy * dy;
    }
    // rest of the code doesn't care about point format

    // basic distance-based simplification
    private simplifyRadialDist(points, sqTolerance) {

        let prevPoint = points[0],
            point;
        const newPoints = [prevPoint];

        for (let i = 1, len = points.length; i < len; i++) {
            point = points[i];

            if (this.getSqDist(point, prevPoint) > sqTolerance) {
                newPoints.push(point);
                prevPoint = point;
            }
        }

        if (prevPoint !== point) {
            newPoints.push(point);
        }

        return newPoints;
    }

    private simplifyDPStep(points, first, last, sqTolerance, simplified) {
        let maxSqDist = sqTolerance,
            index: number;

        for (let i = first + 1; i < last; i++) {
            const sqDist = this.getSqSegDist(points[i], points[first], points[last]);

            if (sqDist > maxSqDist) {
                index = i;
                maxSqDist = sqDist;
            }
        }

        if (maxSqDist > sqTolerance) {
            if (index - first > 1) {
                this.simplifyDPStep(points, first, index, sqTolerance, simplified);
            }
            simplified.push(points[index]);
            if (last - index > 1) {
                this.simplifyDPStep(points, index, last, sqTolerance, simplified);
            }
        }
    }

    // simplification using Ramer-Douglas-Peucker algorithm
    private simplifyDouglasPeucker(points, sqTolerance) {
        const last = points.length - 1;

        const simplified = [points[0]];
        this.simplifyDPStep(points, 0, last, sqTolerance, simplified);
        simplified.push(points[last]);

        return simplified;
    }

    // both algorithms combined for awesome performance
    simplify(points, tolerance, highestQuality) {

        if (points.length <= 2) {
            return points;
        }

        const sqTolerance = tolerance !== undefined ? tolerance * tolerance : 1;

        points = highestQuality ? points : this.simplifyRadialDist(points, sqTolerance);
        points = this.simplifyDouglasPeucker(points, sqTolerance);

        return points;
    }
}

export class SparklineHelperService implements IsparklineHelperService {
    static serviceId: string = 'sparklineHelperService';

    private _xScale: d3.time.Scale<number, number>;
    private _yScale: d3.scale.Linear<number, number>;

    constructor() {
        this._xScale = d3.time.scale();
        this._yScale = d3.scale.linear();
    }

    resize(width: number, height: number): this {
        this._xScale.range([0, width]);
        this._yScale.range([height, 0]);
        return this;
    }

    data(data: StringOrDateValue[]): this {
        const domain: [Date, Date] = d3.extent(data, (d => (new Date(d.timeStamp as string) as any)));
        this._xScale.domain(domain);
        const yDomain = d3.extent(data, d => d.value);
        this._yScale.domain(yDomain).nice();
        return this;
    }

    dataDateDuration(data: { x: number, y: number }[]): this {
        const domain: [Date, Date] = d3.extent(data, (d => (new Date(d.x) as any)));
        this._xScale.domain(domain);
        const yDomain = d3.extent(data, d => d.y);
        this._yScale.domain(yDomain).nice();
        return this;
    }

    xDomain(): [Date, Date];
    xDomain(domain: [Date, Date]): this;
    xDomain(domain?: [Date, Date]): [Date, Date] | this {
        if (domain) {
            this._xScale.domain(domain);
            return this;
        }
        return this._xScale.domain() as [Date, Date];
    }

    yDomain(): [number, number];
    yDomain(domain: [number, number]): this;
    yDomain(domain?: [number, number]): [number, number] | this {
        if (domain) {
            this._yScale.domain(domain).nice();
            return this;
        }
        return this._yScale.domain() as [number, number];
    }

    scaleY(value: number): number {
        return this._yScale(value);
    }

    path(data: StringOrDateValue[], showGap = false): string {
        let converted = data.map(d => {
            return {
                x: this._xScale(new Date(d.timeStamp as string)),
                y: this._yScale(d.value)
            };
        });
        let line = d3.svg.line<{ x: number; y: number }>();

        if (showGap) {
            line = line.interpolate(points => {
                //interpolate straight lines with gaps for NaN
                let section = 0;
                const arrays = [[]];

                points.forEach(d => {
                    if (isNaN(d[1])) {
                        section++;
                        arrays[section] = [];
                    } else {
                        arrays[section].push(d);
                    }
                });

                const pathSections = [];
                arrays.forEach(p => pathSections.push(d3.svg.line()(p)));
                const joined = pathSections.join('');
                //substring becasue D£ always adds an M to a path so we end up with MM at the start
                return joined.substr(1);
            });
        } else {
            converted = new LineSimplifier().simplify(converted, 0.01, true);
        }

        const simplifiedGenerator = line
            .x((d) => d.x)
            .y((d) => d.y);

        return simplifiedGenerator(converted);
    }
}